/**
 * RoomArt — a small illustrated SVG scene per room type. Every scene shares
 * one signature detail: a soft warm amber "lamplight" glow (#E0B47C) that
 * breathes on a ~4s loop, which is what ties the eight rooms together.
 * Kept flat-shaded and simple — the goal is "a lived-in room glimpsed
 * through a doorway," not clip art.
 *
 * Palette used throughout:
 *   background: warm dark gradient  #37291F → #191410
 *   glow warm:  #E0B47C  (breathing)
 *   glow soft:  #B3895A  (brass)
 *   wood/ledge: #4A3222 / #6B4526
 *   cream:      #ECE1CD  (labels, paper)
 *   ink lines:  #14100C
 */

const artProps = {
  viewBox: "0 0 200 250",
  preserveAspectRatio: "xMidYMid slice",
  xmlns: "http://www.w3.org/2000/svg",
  style: { position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" },
  "aria-hidden": true,
};

/* -------- shared building blocks -------- */

// Warm gradient background — stops are driven by CSS variables so the
// room walls flip when the user switches to light mode (see :root
// / :root[data-theme="light"] in index.css). Furniture (books, VHS tapes,
// ledges, etc.) keeps its own colours because an object isn't a theme.
function BgGradient({ id }) {
  return (
    <defs>
      <linearGradient id={`bg-${id}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   style={{ stopColor: "var(--room-bg-top)" }} />
        <stop offset="100%" style={{ stopColor: "var(--room-bg-bottom)" }} />
      </linearGradient>
      <radialGradient id={`glow-${id}`} cx="50%" cy="50%" r="50%">
        <stop offset="0%"   style={{ stopColor: "var(--room-glow)", stopOpacity: 0.95 }} />
        <stop offset="45%"  style={{ stopColor: "var(--room-glow)", stopOpacity: 0.35 }} />
        <stop offset="100%" style={{ stopColor: "var(--room-glow)", stopOpacity: 0 }} />
      </radialGradient>
    </defs>
  );
}

// Breathing warm lamplight glow — the signature detail.
function Glow({ id, cx, cy, r, extraClass = "" }) {
  return (
    <ellipse
      cx={cx} cy={cy} rx={r} ry={r * 0.85}
      fill={`url(#glow-${id})`}
      className={`ra-glow ${extraClass}`}
    />
  );
}

// Hanging lamp (cord + trapezoid shade). Used in Movies / Books / Articles /
// Photos / Recipes. Cord runs from the top edge; shade is warm inside.
function HangingLamp({ x, y, id }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      {/* cord */}
      <line x1="0" y1="-90" x2="0" y2="0" stroke="#2A1F17" strokeWidth="0.8" />
      {/* shade — trapezoid, wider at the bottom */}
      <path
        d="M -14 0  L 14 0  L 20 14  L -20 14  Z"
        fill="#2A1F17"
        stroke="#4A3222"
        strokeWidth="0.6"
      />
      {/* inner warm rim inside the shade */}
      <path
        d="M -18 12.5  L 18 12.5  L 20 14  L -20 14  Z"
        fill="#E0B47C"
        opacity="0.5"
      />
      {/* the actual light source — a small warm dot at the bottom of the shade */}
      <ellipse cx="0" cy="14" rx="7" ry="2.5" fill={`url(#glow-${id})`} opacity="0.95" />
    </g>
  );
}

// Wood ledge / shelf — used by Movies + Books.
function Ledge({ y = 200, id }) {
  return (
    <g>
      <rect x="10" y={y} width="180" height="6" fill="#3A2618" />
      <rect x="10" y={y} width="180" height="1.5" fill="#5C3B23" opacity="0.55" />
      <rect x="10" y={y + 5} width="180" height="1" fill="#0E0906" opacity="0.7" />
    </g>
  );
}

/* ============================================================
 *  1. MOVIES — hanging lamp + 3 VHS tapes on a shelf w/ rec dots
 * ============================================================ */
export function MovieArt() {
  const id = "mov";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      {/* soft ambient glow above the tapes */}
      <Glow id={id} cx={100} cy={140} r={80} />


      {/* three VHS tapes leaning on the ledge */}
      <g>
        {/* left tape — leans left */}
        <g transform="translate(52 200) rotate(-8)">
          <rect x="-22" y="-52" width="44" height="52" rx="1.5" fill="#1A1310" stroke="#3D2A1E" strokeWidth="0.6" />
          <rect x="-18" y="-46" width="36" height="24" rx="0.5" fill="#E4D3B0" />
          <rect x="-18" y="-46" width="36" height="6" fill="#3A2A1F" />
          {/* rec dot */}
          <circle cx="14" cy="-9" r="1.6" fill="#E0B47C" />
        </g>
        {/* centre tape — standing straight */}
        <g transform="translate(100 200)">
          <rect x="-24" y="-58" width="48" height="58" rx="1.5" fill="#1A1310" stroke="#3D2A1E" strokeWidth="0.6" />
          <rect x="-20" y="-52" width="40" height="26" rx="0.5" fill="#E4D3B0" />
          <rect x="-20" y="-52" width="40" height="6" fill="#3A2A1F" />
          <circle cx="16" cy="-9" r="1.6" fill="#8CBB6A" />
        </g>
        {/* right tape — leans right */}
        <g transform="translate(148 200) rotate(9)">
          <rect x="-22" y="-50" width="44" height="50" rx="1.5" fill="#1A1310" stroke="#3D2A1E" strokeWidth="0.6" />
          <rect x="-18" y="-44" width="36" height="22" rx="0.5" fill="#E4D3B0" />
          <rect x="-18" y="-44" width="36" height="6" fill="#3A2A1F" />
          <circle cx="14" cy="-9" r="1.6" fill="#C86A5C" />
        </g>
      </g>

      <Ledge id={id} />
    </svg>
  );
}

/* ============================================================
 *  2. TV SERIES — CRT silhouette w/ flickering screen + glow
 * ============================================================ */
export function TvArt() {
  const id = "tv";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      {/* warm glow behind the TV */}
      <Glow id={id} cx={100} cy={130} r={80} />

      {/* TV body */}
      <g transform="translate(100 145)">
        <rect x="-58" y="-40" width="116" height="76" rx="6" fill="#221812" stroke="#3D2A1E" strokeWidth="0.7" />
        {/* screen (flickers) */}
        <rect x="-46" y="-30" width="82" height="56" rx="3" fill="#0E0A08" />
        <rect x="-46" y="-30" width="82" height="56" rx="3" fill="#E0B47C" opacity="0.16" className="ra-tv-flicker" />
        {/* scan lines */}
        <g opacity="0.18">
          <line x1="-46" y1="-16" x2="36" y2="-16" stroke="#E0B47C" strokeWidth="0.4" />
          <line x1="-46" y1="0" x2="36" y2="0" stroke="#E0B47C" strokeWidth="0.4" />
          <line x1="-46" y1="16" x2="36" y2="16" stroke="#E0B47C" strokeWidth="0.4" />
        </g>
        {/* knobs on the right */}
        <circle cx="46" cy="-12" r="4" fill="#3D2A1E" stroke="#6B4526" strokeWidth="0.5" />
        <circle cx="46" cy="10" r="4" fill="#3D2A1E" stroke="#6B4526" strokeWidth="0.5" />
        <line x1="46" y1="-15" x2="46" y2="-9" stroke="#E0B47C" strokeWidth="0.6" />
        <line x1="43" y1="10" x2="49" y2="10" stroke="#E0B47C" strokeWidth="0.6" />
      </g>

      {/* short splayed legs */}
      <g stroke="#3D2A1E" strokeWidth="2" strokeLinecap="round">
        <line x1="60" y1="181" x2="52" y2="196" />
        <line x1="140" y1="181" x2="148" y2="196" />
      </g>
    </svg>
  );
}

/* ============================================================
 *  3. BOOKS — hanging lamp above 4 book spines + a small plant
 * ============================================================ */
export function BookArt() {
  const id = "bk";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      <Glow id={id} cx={100} cy={140} r={80} />

      {/* four books of varying height on the ledge */}
      <g>
        {/* oxblood */}
        <rect x="42" y="140" width="20" height="60" fill="#6E2A2A" stroke="#0E0906" strokeWidth="0.5" />
        <line x1="42" y1="150" x2="62" y2="150" stroke="#2A0F0F" strokeWidth="0.5" />
        <line x1="42" y1="192" x2="62" y2="192" stroke="#2A0F0F" strokeWidth="0.5" />
        {/* forest green */}
        <rect x="64" y="130" width="22" height="70" fill="#345C42" stroke="#0E0906" strokeWidth="0.5" />
        <line x1="64" y1="140" x2="86" y2="140" stroke="#122417" strokeWidth="0.5" />
        <line x1="64" y1="192" x2="86" y2="192" stroke="#122417" strokeWidth="0.5" />
        {/* mustard */}
        <rect x="88" y="146" width="18" height="54" fill="#B0873A" stroke="#0E0906" strokeWidth="0.5" />
        <line x1="88" y1="156" x2="106" y2="156" stroke="#4A381A" strokeWidth="0.5" />
        <line x1="88" y1="192" x2="106" y2="192" stroke="#4A381A" strokeWidth="0.5" />
        {/* navy */}
        <rect x="108" y="134" width="24" height="66" fill="#2A3E5C" stroke="#0E0906" strokeWidth="0.5" />
        <line x1="108" y1="144" x2="132" y2="144" stroke="#101B2E" strokeWidth="0.5" />
        <line x1="108" y1="192" x2="132" y2="192" stroke="#101B2E" strokeWidth="0.5" />
      </g>

      {/* small potted plant to the right of the books */}
      <g transform="translate(154 200)">
        {/* pot */}
        <path d="M -10 0 L 10 0 L 8 -14 L -8 -14 Z" fill="#6B4526" stroke="#0E0906" strokeWidth="0.5" />
        <rect x="-11" y="-16" width="22" height="3" fill="#5C3B23" />
        {/* leaves */}
        <ellipse cx="-4" cy="-24" rx="5" ry="10" fill="#4E6B4A" transform="rotate(-18 -4 -24)" />
        <ellipse cx="4" cy="-26" rx="5" ry="12" fill="#5E7E58" transform="rotate(15 4 -26)" />
        <ellipse cx="0" cy="-30" rx="4" ry="9" fill="#446548" />
      </g>

      <Ledge id={id} />
    </svg>
  );
}

/* ============================================================
 *  4. MUSIC — vinyl record + tonearm, record rotates ~9s/turn
 * ============================================================ */
export function MusicArt() {
  const id = "mu";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      {/* warm ambient glow behind the record */}
      <Glow id={id} cx={95} cy={130} r={95} />

      {/* the record itself — group rotates */}
      <g className="ra-vinyl" style={{ transformOrigin: "95px 130px" }}>
        <circle cx="95" cy="130" r="72" fill="#0F0B08" stroke="#3D2A1E" strokeWidth="0.6" />
        {/* grooves */}
        <g stroke="#2A1F17" strokeWidth="0.4" fill="none">
          <circle cx="95" cy="130" r="64" />
          <circle cx="95" cy="130" r="56" />
          <circle cx="95" cy="130" r="48" />
          <circle cx="95" cy="130" r="40" />
          <circle cx="95" cy="130" r="32" />
        </g>
        {/* highlight groove */}
        <circle cx="95" cy="130" r="60" stroke="#E0B47C" strokeWidth="0.3" fill="none" opacity="0.35" />
        {/* centre label */}
        <circle cx="95" cy="130" r="22" fill="#B0553A" />
        <circle cx="95" cy="130" r="16" fill="#8C3E2A" />
        <text x="95" y="132" textAnchor="middle" fontFamily="'IBM Plex Mono', monospace" fontSize="5" fill="#ECE1CD" opacity="0.75">SIDE A</text>
        {/* centre spindle */}
        <circle cx="95" cy="130" r="1.6" fill="#ECE1CD" opacity="0.6" />
      </g>

      {/* tonearm — anchored top-right, resting on the record */}
      <g>
        <circle cx="172" cy="70" r="6" fill="#3D2A1E" stroke="#6B4526" strokeWidth="0.5" />
        <circle cx="172" cy="70" r="2.5" fill="#6B4526" />
        <line x1="172" y1="70" x2="128" y2="120" stroke="#5C3B23" strokeWidth="2.5" strokeLinecap="round" />
        {/* cartridge / headshell */}
        <rect x="120" y="118" width="12" height="6" fill="#2A1F17" transform="rotate(-42 126 121)" />
      </g>
    </svg>
  );
}

/* ============================================================
 *  5. ARTICLES — hanging lamp + stack of 3 cards + clip + pencil
 * ============================================================ */
export function ArticleArt() {
  const id = "ar";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      <Glow id={id} cx={100} cy={145} r={80} />

      {/* three overlapping index cards on a surface */}
      <g>
        {/* bottom card — slight rotation */}
        <g transform="translate(100 170) rotate(-5)">
          <rect x="-52" y="-30" width="104" height="60" rx="2" fill="#EFE1C3" stroke="#8C6A3A" strokeWidth="0.4" />
          <line x1="-46" y1="-14" x2="46" y2="-14" stroke="#B0885A" strokeWidth="0.35" opacity="0.55" />
          <line x1="-46" y1="-4" x2="46" y2="-4" stroke="#B0885A" strokeWidth="0.35" opacity="0.55" />
          <line x1="-46" y1="6" x2="46" y2="6" stroke="#B0885A" strokeWidth="0.35" opacity="0.55" />
          <line x1="-46" y1="16" x2="46" y2="16" stroke="#B0885A" strokeWidth="0.35" opacity="0.55" />
        </g>
        {/* middle card */}
        <g transform="translate(103 164) rotate(3)">
          <rect x="-50" y="-28" width="100" height="58" rx="2" fill="#F1E7D2" stroke="#8C6A3A" strokeWidth="0.4" />
          <line x1="-44" y1="-12" x2="44" y2="-12" stroke="#B0885A" strokeWidth="0.35" opacity="0.55" />
          <line x1="-44" y1="-2" x2="44" y2="-2" stroke="#B0885A" strokeWidth="0.35" opacity="0.55" />
          <line x1="-44" y1="8" x2="44" y2="8" stroke="#B0885A" strokeWidth="0.35" opacity="0.55" />
        </g>
        {/* top card — the one being read */}
        <g transform="translate(98 158)">
          <rect x="-50" y="-28" width="100" height="58" rx="2" fill="#F6ECD6" stroke="#8C6A3A" strokeWidth="0.4" />
          <line x1="-44" y1="-12" x2="44" y2="-12" stroke="#8C6A3A" strokeWidth="0.4" opacity="0.7" />
          <line x1="-44" y1="-2" x2="30" y2="-2" stroke="#8C6A3A" strokeWidth="0.35" opacity="0.55" />
          <line x1="-44" y1="8" x2="40" y2="8" stroke="#8C6A3A" strokeWidth="0.35" opacity="0.55" />
          <line x1="-44" y1="18" x2="20" y2="18" stroke="#8C6A3A" strokeWidth="0.35" opacity="0.55" />
          {/* binder clip at the top */}
          <g transform="translate(-10 -28)">
            <rect x="-8" y="-4" width="16" height="6" fill="#3D2A1E" stroke="#0E0906" strokeWidth="0.4" />
            <rect x="-6" y="-2" width="12" height="2" fill="#5C3B23" />
            <path d="M -6 -4 Q -8 -10 0 -10 Q 8 -10 6 -4" stroke="#3D2A1E" strokeWidth="0.8" fill="none" />
          </g>
        </g>
        {/* pencil resting diagonally across */}
        <g transform="translate(120 176) rotate(-24)">
          <rect x="-24" y="-2" width="46" height="4" fill="#E4B54D" stroke="#3D2A1E" strokeWidth="0.35" />
          <polygon points="22,-2 22,2 28,0" fill="#F1E7D2" stroke="#3D2A1E" strokeWidth="0.35" />
          <polygon points="26.5,-0.8 26.5,0.8 28,0" fill="#2A1F17" />
          <rect x="-26" y="-2" width="4" height="4" fill="#C86A5C" />
        </g>
      </g>
    </svg>
  );
}

/* ============================================================
 *  6. VIDEOS — projector w/ rotating reel + light-beam cone glow
 * ============================================================ */
export function VideoArt() {
  const id = "vi";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      {/* Signature warm glow — small, behind the projector body, so every
       * hall panel carries the shared .ra-glow breathing element even when
       * the visible warmth mostly comes from the projector's light beam. */}
      <Glow id={id} cx={60} cy={140} r={44} />

      {/* light beam cone — the visible warmth points to the right from the
       * lens. Pulses on its own 4s cycle via .ra-beam. */}
      <path
        d="M 78 132 L 200 78 L 200 186 L 78 148 Z"
        fill={`url(#glow-${id})`}
        opacity="0.75"
        className="ra-beam"
      />

      {/* projector body — profile, left side */}
      <g transform="translate(60 140)">
        {/* base */}
        <rect x="-38" y="18" width="72" height="8" rx="1" fill="#3D2A1E" />
        {/* main box */}
        <rect x="-34" y="-14" width="60" height="32" rx="2" fill="#2A1F17" stroke="#4A3222" strokeWidth="0.6" />
        {/* front lens barrel — points right */}
        <rect x="20" y="-6" width="14" height="16" fill="#3D2A1E" stroke="#6B4526" strokeWidth="0.5" />
        <rect x="30" y="-4" width="4" height="12" fill="#0E0906" />
        {/* the actual bright lens dot */}
        <circle cx="32" cy="2" r="2.5" fill="#E0B47C" />

        {/* upper reel — rotates */}
        <g className="ra-reel" style={{ transformOrigin: "-8px -22px" }}>
          <circle cx="-8" cy="-22" r="16" fill="#1A1310" stroke="#4A3222" strokeWidth="0.6" />
          <circle cx="-8" cy="-22" r="4" fill="#3D2A1E" />
          {/* spokes */}
          <line x1="-8" y1="-38" x2="-8" y2="-6" stroke="#3D2A1E" strokeWidth="1" />
          <line x1="-24" y1="-22" x2="8" y2="-22" stroke="#3D2A1E" strokeWidth="1" />
          <line x1="-19.3" y1="-33.3" x2="3.3" y2="-10.7" stroke="#3D2A1E" strokeWidth="1" />
          <line x1="3.3" y1="-33.3" x2="-19.3" y2="-10.7" stroke="#3D2A1E" strokeWidth="1" />
          {/* rim highlight */}
          <circle cx="-8" cy="-22" r="14" fill="none" stroke="#6B4526" strokeWidth="0.4" opacity="0.7" />
        </g>

        {/* small nub connecting reel to body */}
        <rect x="-10" y="-14" width="4" height="4" fill="#4A3222" />
      </g>
    </svg>
  );
}

/* ============================================================
 *  7. PHOTO LIBRARY — photo box + 2 leaning prints + hanging lamp
 * ============================================================ */
export function PictureArt() {
  const id = "pi";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      <Glow id={id} cx={100} cy={150} r={80} />

      {/* photo box — squat rectangle */}
      <g transform="translate(112 175)">
        <rect x="-40" y="-10" width="80" height="34" rx="1" fill="#5C3B23" stroke="#2A1F17" strokeWidth="0.6" />
        {/* lid strip */}
        <rect x="-40" y="-10" width="80" height="5" fill="#6B4526" />
        <rect x="-40" y="-6" width="80" height="0.6" fill="#0E0906" opacity="0.7" />
        {/* front label / hole */}
        <rect x="-8" y="6" width="16" height="6" rx="0.6" fill="#2A1F17" />
      </g>

      {/* two photo prints leaning against the left of the box */}
      <g>
        {/* farther print */}
        <g transform="translate(60 152) rotate(-14)">
          <rect x="-18" y="-24" width="36" height="48" fill="#ECE1CD" stroke="#8C6A3A" strokeWidth="0.4" />
          <rect x="-16" y="-22" width="32" height="34" fill="#7B9BC0" opacity="0.55" />
          {/* folded corner */}
          <path d="M 12 -22 L 18 -22 L 18 -16 Z" fill="#D8CDB4" stroke="#8C6A3A" strokeWidth="0.3" />
        </g>
        {/* nearer print */}
        <g transform="translate(72 158) rotate(-6)">
          <rect x="-18" y="-24" width="36" height="48" fill="#F4EAD1" stroke="#8C6A3A" strokeWidth="0.4" />
          <rect x="-16" y="-22" width="32" height="34" fill="#B08560" opacity="0.55" />
          <path d="M 12 -22 L 18 -22 L 18 -16 Z" fill="#DFD3B8" stroke="#8C6A3A" strokeWidth="0.3" />
        </g>
      </g>
    </svg>
  );
}

/* ============================================================
 *  8. RECIPES — open wooden recipe box, tabs, whisk, hanging lamp
 * ============================================================ */
export function RecipeArt() {
  const id = "re";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      <Glow id={id} cx={100} cy={150} r={82} />

      {/* recipe box, open — box body + open lid tilted back */}
      <g transform="translate(100 178)">
        {/* open lid — leaning back, behind */}
        <path d="M -36 -6 L 36 -6 L 44 -34 L -44 -34 Z" fill="#4A3222" stroke="#2A1F17" strokeWidth="0.6" />
        <path d="M -34 -8 L 34 -8 L 40 -30 L -40 -30 Z" fill="#5C3B23" opacity="0.55" />

        {/* box body */}
        <rect x="-38" y="-6" width="76" height="30" rx="1" fill="#6B4526" stroke="#2A1F17" strokeWidth="0.6" />
        <rect x="-38" y="-6" width="76" height="4" fill="#4A3222" />

        {/* index cards standing inside the box, staggered heights */}
        <g>
          <rect x="-26" y="-24" width="20" height="24" fill="#F1E7D2" stroke="#8C6A3A" strokeWidth="0.4" />
          <rect x="-4" y="-28" width="20" height="28" fill="#F6ECD6" stroke="#8C6A3A" strokeWidth="0.4" />
          <rect x="18" y="-22" width="12" height="22" fill="#EFE1C3" stroke="#8C6A3A" strokeWidth="0.4" />
          {/* tabbed divider */}
          <g>
            <rect x="-16" y="-30" width="14" height="6" fill="#B0873A" stroke="#4A381A" strokeWidth="0.35" />
            <rect x="-16" y="-24" width="14" height="24" fill="none" stroke="#B0873A" strokeWidth="0.5" opacity="0.75" />
          </g>
          {/* second tab */}
          <g>
            <rect x="6" y="-32" width="10" height="5" fill="#8C3E2A" stroke="#4A181C" strokeWidth="0.35" />
          </g>
        </g>
      </g>

      {/* whisk leaning against the right side of the box */}
      <g transform="translate(158 178) rotate(18)">
        {/* handle */}
        <rect x="-2" y="-40" width="4" height="26" rx="0.6" fill="#5C3B23" stroke="#2A1F17" strokeWidth="0.35" />
        {/* cap */}
        <rect x="-3" y="-42" width="6" height="4" fill="#3D2A1E" />
        {/* wires — a few curved loops */}
        <g fill="none" stroke="#C8BBA1" strokeWidth="0.8" strokeLinecap="round" opacity="0.85">
          <path d="M -2 -14 Q -6 -6 0 4" />
          <path d="M 0 -14 Q 0 -4 0 4" />
          <path d="M 2 -14 Q 6 -6 0 4" />
          <path d="M -1 -14 Q -3 -4 -1 4" />
          <path d="M 1 -14 Q 3 -4 1 4" />
        </g>
      </g>
    </svg>
  );
}

/* ============================================================
 *  9. THE COMMONS — long café table + two mugs + open notebook,
 *  hanging lamp glowing above. A warm gathering place feel.
 * ============================================================ */
export function CommonsArt() {
  const id = "co";
  return (
    <svg {...artProps}>
      <BgGradient id={id} />
      <rect width="200" height="250" fill={`url(#bg-${id})`} />

      {/* soft ambient glow above the table — signature breathing */}
      <Glow id={id} cx={100} cy={145} r={85} />

      {/* long table — a slab with two visible legs */}
      <g>
        {/* tabletop */}
        <rect x="18" y="176" width="164" height="10" fill="#5C3B23" stroke="#2A1F17" strokeWidth="0.6" />
        <rect x="18" y="176" width="164" height="2" fill="#7A5230" opacity="0.6" />
        {/* front edge shadow */}
        <rect x="18" y="184" width="164" height="1.2" fill="#0E0906" opacity="0.7" />
        {/* legs */}
        <rect x="28" y="186" width="6" height="30" fill="#3D2A1E" />
        <rect x="166" y="186" width="6" height="30" fill="#3D2A1E" />
      </g>

      {/* two chair backs peeking above/behind the table (silhouettes) */}
      <g fill="#2A1F17" stroke="#4A3222" strokeWidth="0.5">
        <rect x="42" y="150" width="24" height="26" />
        <rect x="46" y="146" width="16" height="6" />
        <rect x="134" y="150" width="24" height="26" />
        <rect x="138" y="146" width="16" height="6" />
      </g>

      {/* mug on the left — steam wisps */}
      <g transform="translate(80 172)">
        <path d="M -8 0 L 8 0 L 6 -14 L -6 -14 Z" fill="#B08560" stroke="#2A1F17" strokeWidth="0.5" />
        <ellipse cx="0" cy="-14" rx="6" ry="1.4" fill="#3E2A18" />
        {/* handle */}
        <path d="M 8 -4 Q 13 -4 13 -9 Q 13 -13 8 -13" stroke="#B08560" strokeWidth="1.4" fill="none" />
        {/* steam */}
        <g stroke="#E0B47C" strokeWidth="0.7" fill="none" opacity="0.55">
          <path d="M -2 -18 Q -4 -22 -2 -26 Q 0 -30 -2 -34" />
          <path d="M 2 -19 Q 4 -23 2 -27 Q 0 -31 2 -35" />
        </g>
      </g>

      {/* open notebook centre-right */}
      <g transform="translate(112 174)">
        {/* spread */}
        <path d="M -18 0 L 18 0 L 20 -12 L -20 -12 Z" fill="#F1E7D2" stroke="#8C6A3A" strokeWidth="0.4" />
        {/* spine crease */}
        <line x1="0" y1="0" x2="0" y2="-12" stroke="#8C6A3A" strokeWidth="0.4" opacity="0.55" />
        {/* ruled lines */}
        <g stroke="#B0885A" strokeWidth="0.3" opacity="0.55">
          <line x1="-16" y1="-8" x2="-2" y2="-8" />
          <line x1="-16" y1="-4" x2="-4" y2="-4" />
          <line x1="2" y1="-8" x2="16" y2="-8" />
          <line x1="2" y1="-4" x2="14" y2="-4" />
        </g>
      </g>

      {/* second mug on the right — no steam, resting */}
      <g transform="translate(148 174)">
        <path d="M -7 0 L 7 0 L 6 -11 L -6 -11 Z" fill="#8C3E2A" stroke="#2A1F17" strokeWidth="0.5" />
        <ellipse cx="0" cy="-11" rx="5" ry="1.2" fill="#3E1A10" />
        <path d="M 7 -3 Q 11 -3 11 -8 Q 11 -11 7 -11" stroke="#8C3E2A" strokeWidth="1.2" fill="none" />
      </g>
    </svg>
  );
}

/* ============================================================
 *  Dispatch — returns the right art for a room type.
 * ============================================================ */
export default function RoomArt({ type }) {
  switch (type) {
    // Movies and Videos swapped per user request — the projector-reel +
    // beam animation now lives in the Movies entrance, and the VHS-tapes
    // tableau greets the Videos room. The underlying components are
    // unchanged; only the dispatch mapping is flipped.
    case "movie":   return <VideoArt />;
    case "tvshow":  return <TvArt />;
    case "book":    return <BookArt />;
    case "music":   return <MusicArt />;
    case "article": return <ArticleArt />;
    case "video":   return <MovieArt />;
    case "picture": return <PictureArt />;
    case "recipe":  return <RecipeArt />;
    case "commons": return <CommonsArt />;
    default:        return null;
  }
}
